import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { LANGUAGES, languageOf, rememberLocale, type Locale } from "../i18n/languages";
import { localeHref, type RoutePath } from "../routes";

export interface LanguageMenuProps {
  locale: Locale;
  /** The current page, so every entry switches language without leaving it. */
  path: RoutePath;
}

/**
 * Built on <details>, not on a button plus JavaScript state.
 *
 * The page is pre-rendered and mostly static, so the switcher has to work before — or
 * entirely without — hydration; <details> opens natively, is keyboard-operable and is
 * announced correctly by screen readers. The effect below only adds the two conveniences the
 * element does not give for free: closing on Escape and on an outside click.
 */
export function LanguageMenu({ locale, path }: LanguageMenuProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDetailsElement>(null);
  const current = languageOf(locale);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const close = () => element.removeAttribute("open");

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && element.open) {
        close();
        element.querySelector("summary")?.focus();
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      if (element.open && !element.contains(event.target as Node)) close();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, []);

  return (
    <details className="lang" ref={ref}>
      <summary className="lang__toggle" title={t("nav.languageLabel")}>
        <svg
          className="lang__globe"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3Z" />
        </svg>
        <span className="lang__current">{current.endonym}</span>
      </summary>

      <ul className="lang__list" aria-label={t("nav.languageLabel")}>
        {LANGUAGES.map((language) => (
          <li key={language.code}>
            <a
              href={localeHref(language.code, path)}
              lang={language.code}
              dir={language.dir}
              hrefLang={language.code}
              aria-current={language.code === locale ? "true" : undefined}
              // Written here as well as on arrival so the choice survives even if the next
              // page's script never runs. The link still navigates; this only records it.
              onClick={() => rememberLocale(language.code)}
            >
              {language.endonym}
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
}
